import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { RwAlert, RwButton } from '@/common/presentation/components/rw';

export interface LibraryGalleryLoadMoreProps {
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  /** See `LibraryGalleryFooter` — the grid's `isRefreshing`: "Load more" stays in place but disabled. */
  isRefreshing: boolean;
  /**
   * Set when fetching the NEXT page failed while earlier pages are still on screen. The control then
   * becomes an inline `role="alert"` with "Try again" (which calls `onLoadMore` again) instead of the
   * "Load more" button; the grid above it stays put.
   */
  loadMoreError: string | null;
  testIdPrefix: string;
}

/** True when keyboard focus is nowhere useful: on nothing / the body, or on a control inside `root`. */
function isFocusLostOrInside(root: HTMLElement | null): boolean {
  const active = document.activeElement;
  if (!active || active === document.body) return true;
  return Boolean(root?.contains(active));
}

/**
 * The gallery's next-page control: "Load more", or — after a failed next-page fetch — an inline
 * error with "Try again".
 *
 * Focus is managed on every transition, because the control that had focus unmounts under the
 * keyboard user: Load more -> Try again (the failure), Try again -> Load more (the recovery) and
 * Try again -> nothing left to load (the recovery that loaded the LAST page). That last one keeps the
 * component mounted just long enough to show a focusable "All items loaded" status; a last page that
 * was reached WITHOUT a prior error still unmounts silently. Focus only moves when it would
 * otherwise be lost, never away from something the user chose.
 */
export function LibraryGalleryLoadMore({
  hasMore,
  onLoadMore,
  isLoadingMore,
  isRefreshing,
  loadMoreError,
  testIdPrefix
}: LibraryGalleryLoadMoreProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  const doneRef = useRef<HTMLParagraphElement>(null);
  const hadErrorRef = useRef(false);
  const hasError = Boolean(loadMoreError);

  // "Finished after an error" latch, adjusted during render (the React "adjusting state when a prop
  // changes" pattern) so the status node mounts in the same commit that unmounts "Try again". It
  // clears as soon as there is more to load again, or a refetch / new filter is in flight.
  const [prevHasError, setPrevHasError] = useState(false);
  const [finishedAfterError, setFinishedAfterError] = useState(false);
  if (hasError !== prevHasError) {
    setPrevHasError(hasError);
    setFinishedAfterError(prevHasError && !hasMore);
  } else if (finishedAfterError && (hasMore || isRefreshing)) {
    setFinishedAfterError(false);
  }

  useEffect(() => {
    const hadError = hadErrorRef.current;
    hadErrorRef.current = hasError;
    if (!hasError && !hadError) return;
    if (!isFocusLostOrInside(rootRef.current)) return;
    if (hasError) {
      rootRef.current?.querySelector('button')?.focus();
      return;
    }
    (loadMoreRef.current ?? doneRef.current)?.focus();
  }, [hasError]);

  const handleTryAgain = () => {
    // `fetchNextPage` cancels an in-flight fetch by default, so a second click mid-retry would abort it.
    // The button is only `aria-busy`, never `disabled`: it is the focused element and must keep focus.
    if (isLoadingMore || isRefreshing) return;
    onLoadMore();
  };

  if (!hasError && !hasMore && !finishedAfterError) return null;

  const renderControl = () => {
    if (loadMoreError) {
      return (
        <RwAlert
          tone="critical"
          title={loadMoreError}
          description={isLoadingMore ? 'Trying again…' : undefined}
          actionLabel="Try again"
          actionBusy={isLoadingMore}
          onAction={handleTryAgain}
          actionTestId={`${testIdPrefix}:load-more-retry`}
          data-testid={`${testIdPrefix}:load-more-error`}
        />
      );
    }
    if (!hasMore) {
      return (
        <Typography
          ref={doneRef}
          role="status"
          aria-live="polite"
          tabIndex={-1}
          variant="body2"
          data-testid={`${testIdPrefix}:load-more-done`}
        >
          All items loaded
        </Typography>
      );
    }
    return (
      <RwButton
        ref={loadMoreRef}
        tier="plain-neutral"
        size="md"
        onClick={onLoadMore}
        loading={isLoadingMore}
        disabled={isRefreshing}
        data-testid={`${testIdPrefix}:load-more`}
      >
        Load more
      </RwButton>
    );
  };

  return (
    <Box ref={rootRef} sx={{ display: 'contents' }}>
      {renderControl()}
    </Box>
  );
}
